// tslint:disable: max-line-length

import { ApiService } from '../services/api_service';
import { ContextService } from '../services/context_service';
import { DataService } from '../services/data_service';
import { getBetrieblicheSituation, getGewerk, getOrtGTZ } from './functions/getGewerk';
import { getCO2Scope3StructureAllgemein, getCO2Scope3StructureNachgelagert, getCO2Scope3StructureVorgelagert } from '../bereiche/dateneingabe/functions/getCO2Scope3Structure';
import { getVsmeBasisEntities } from '../bereiche/dateneingabe/assistent/items/assistent_config_vsme_basis';
import { getVsmeBpmEntities } from '../bereiche/dateneingabe/assistent/items/assistent_config_vsme_bpm';
import { getVsmePatEntities } from '../bereiche/dateneingabe/assistent/items/assistent_config_vsme_pat';
import { getDefaultKennzahlenDefinition } from '../helpers/getDefaultKennzahlenDefinition';
import { getWitterungsFaktor } from '../bereiche/dateneingabe/functions/getWitterungsFaktor';
import { IDs } from '../helpers/IDs';
import { lazyInject } from '../services/inversify';
import { processCO2Mehrkosten } from './functions/processCO2Mehrkosten';
import { processHinweise } from './functions/processHinweise';
import { processKennzahlenExport } from '../main/functions/processKennzahlenExport';
// import { processKennzahlenImport } from './functions/processKennzahlenImport';
import { processKennziffernBerechnung } from './functions/processKennzahlenBerechnng';
import { processPlausibilisierung } from './functions/processPlausibilisierung';
import { processSteuer } from './functions/processSteuer';
import { processThg } from './functions/thg/processThg';
import { processVsme } from './functions/vsme/processVsme';
import { processWetterstationen } from './functions/processWetterstationen';
import { processZertifizierung } from './functions/processZertifizierung';
import { storePlausiData } from './functions/plausibilisierung';
import { StructureService } from '../services/structure_service';
import { TriggerService } from '../core/trigger/trigger_service';
import { Main } from './Main';

export class MainFilterLoader {

    constructor(callback: (data: any) => void, callbackProgress: (progress: number) => void) {
        this.loadData = this.loadData.bind(this);
        this.dataLoaded = this.dataLoaded.bind(this);
        this.processCall = this.processCall.bind(this);

        this.module = IDs.MOD_ETOOL;
        this.callback = callback;
        this.callbackProgress = callbackProgress;
        this.registerLocalDataUpdate();
    }

    private callback: (data: any) => void;
    private callbackProgress: (progress: number) => void;
    private data: any = {};
    private loadCnt = 0;
    private loadCntGewerk = 0;
    private loadGoal = 0;
    private loadGoalGewerk = 0;
    private module: string;
    private start: number;
    private config: any = require('../wips.config.js');
    private devFeaturesMap: any = this.config.devFeaturesMap || {};
    private checkedTestRegistrierung = false;

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(DataService) private dataService: DataService;
    @lazyInject(StructureService) private structureService: StructureService;
    @lazyInject(TriggerService) private triggerService: TriggerService;

    dataLoaded(result: any, entityName: string, forGewerk?: boolean): void {
        const isAutoWalking = !!Main.autoWalk; // FIXME YW: Es soll hier nicht importiert werden, was React verwendet
        // const isAutoWalking = false;
        if (result === undefined) {
            this.triggerService.fire('reload', entityName);
            return;
        }
        this.data[entityName] = result[entityName];

        if (entityName === IDs.ENT_PLAUSIBILISIERUNG) {
            storePlausiData(result[entityName]);
        }

        if (!isAutoWalking && entityName === IDs.ENT_KONFIGURATION) {
            this.setStandardKonfigurationKurz(result[entityName]);
        }

        if (entityName === IDs.ENT_UNTERNEHMENSDATEN) {
            const gewerk = getGewerk(this.data);
            const bs = getBetrieblicheSituation(this.data);
            const ort_tid = getOrtGTZ(this.data);
            let useCluster = this.contextService.get('benchmarkShowCluster');
            const gewerk_used = this.contextService.get('gewerk_used');
            if (!gewerk_used || gewerk_used !== gewerk) {
                useCluster = false;
                this.contextService.set('benchmarkShowCluster', false);
            }
            this.contextService.set('gewerk_used', gewerk); // Um festzustellen, wenn sich das Gewerk ändert
            this.loadDataForGewerk(useCluster, gewerk, bs, ort_tid);
        }

        if (entityName === IDs.ENT_CO2_SCOPE3_FAKTOREN_MATERIAL) {
            this.data[entityName] = [
                // { tid: -2, name: ' Dienstleistungen' },
                { tid: -1, name: ' Weiteres Material', kategorie_tid_orig: 1, kategorie_tid: 1 },
                ...(this.data[entityName] || [])
            ];
        }

        if (entityName === IDs.ENT_KLIMAGASE_ART || entityName === IDs.ENT_CO2_FAKTOREN_KLIMAGASE) {
            this.processKlimagaseFaktoren();
        }

        if (entityName === IDs.ENT_KENNZAHLEN_DEFINTION) {
            const defaultKennzahlen = getDefaultKennzahlenDefinition()
            if (!this.data[entityName] || this.data[entityName].length === 0) {
                // Wenn nicht vorhanden --> Alle einfügen
                this.data[entityName] = defaultKennzahlen;
                // console.log('Benutze Standard-Kennziffern ...');
            } else {
                // Sonst nur die fehlenden ergänzen
                defaultKennzahlen.forEach((kz: any) => {
                    if (!this.data[entityName].some((kz_check: any) => kz.kuerzel === kz_check.kuerzel)) {
                        this.data[entityName].push(kz);
                    }
                });
            }
        }

        if (!isAutoWalking && (entityName === IDs.ENT_REGISTRIERUNG || entityName === IDs.ENT_KONFIGURATION)) {
            // Einer davon bedeutet, dass die beiden Entitäten geladen wurden.
            // Wir prüfen die Datenvollständigkeit in der Funktion.
            this.checkKonfigKonsis();
        }

        if (entityName === IDs.ENT_KENNZAHLEN_WERTE_AGGREGATION) {
            this.triggerService.fire('kz_aggregation_reload', '');
        }

        if (result.bundle) {
            this.data[`${entityName}_bundle`] = result.bundle;
        }

        if (forGewerk) {
            this.loadCntGewerk++;
            // console.log('GEW', entityName, this.data[entityName]);
        } else {
            this.loadCnt++;
        }

        const base = this.loadGoal + this.loadGoalGewerk;
        const loaded = this.loadCnt + this.loadCntGewerk;
        const progress = base > 0 ? Math.round((100 / base) * loaded) : 0;
        this.callbackProgress(progress);

        // Wir prüfen mit >= da spätere Reloads den Count nochmal erhöhen
        // console.log(this.loadCnt, this.loadGoal);
        if (this.loadCnt >= this.loadGoal && !(this.loadCntGewerk % this.loadGoalGewerk)) {
            console.log(`Laden (${this.loadCnt} : ${this.loadGoal} / ${this.loadCntGewerk} : ${this.loadGoalGewerk}) fertig nach: ${(new Date().getTime() - this.start)} ms`);
            // console.log(this.data);
            this.data['loadDone'] = true;
            this.process();
        }
    }

    loadData(): void {
        // console.log('Load data NOW');
        this.start = new Date().getTime();
        let loadInfo: Array<any> = [
            { entity: IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_BIOMASSE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_KWK, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_PHOTOVOLTAIK, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_SOLARTHERMIE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_WAERMEPUMPEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_WINDKRAFT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ARCHIV_ITEMS, filter: {}, settings: { resolve: true }, module: IDs.MOD_CORE },
            { entity: IDs.ENT_BETRIEBSDATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_DATEIABLAGE_DYNAMISCH, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CUSTOM_ROW_NAME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_EINZELMASSNAHMEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_EIGENSTROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_EIGENWAERME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_STROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_VERBRAEUCHE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WASSER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEKOSTEN_PROJEKTION, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIE_COCKPIT_HINWEISE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CO2_SCOPE3_LIEFERANT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_FAHRZEUGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_FAHRZEUGE_JAHRESWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_GEBAEUDE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_GEWERKE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KENNZAHLEN_DYNAMISCH, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KENNZAHLEN_WERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KONFIGURATION, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_MASCHINEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_MASCHINEN_JAHRESWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_NACE_CODES, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_OEL_ABRECHNUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_OEL_ZAEHLERABLESUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_POPUP_BLOCK, filter: { user: this.apiService.getUsername() }, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_PV_EINGABE, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_REPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_SONSTIGE_DATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_STANDORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_STANDORT_GRUPPE, filter: {}, settings: {}, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_STANDORT_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ZAEHLER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ZERTIFIZIERUNGSOPTIONEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KLIMAGASE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KLIMAGASE_BENUTZER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CO2_SICHERHEITSZUSCHLAG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CO2_SCOPE3_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANGEMIETETE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANGEMIETETE_FAHRZEUGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_EIGENE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANABREISE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CUSTOM_DATENSATZ_REIHENFOLGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // CFM
            { entity: IDs.ENT_CFM_MOTIVATION, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // VSME
            { entity: IDs.ENT_VSME_BPM5_C, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_BPM3_SCOPE1, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_BPM3_SCOPE2, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_EXPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_REPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // EED
            { entity: IDs.ENT_EED_UMFRAGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // ACHTUNG ab hier aus ETOOL_ADMIN
            { entity: IDs.ENT_BRANCHEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_BRANCHEN_BEREICHE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_BRANCHEN_GEWERKE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_GEWERKE_WZ, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_DATEIEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ERZEUGUNGS_LASTDATEN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_FOERDERUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_FOERDERUNG_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARTNER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARTNER_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_GTZ_ORTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_NACE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARAMETER_ENERGIESTEUER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARAMETER_HEIZWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PV_PARAMETER, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_EEG_VERGUETUNG, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_QUELLEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_SYSTEMPARAMETER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PLAUSIBILISIERUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_SYSTEMTEXTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_SYSTEMTEXTE_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ZERTIFIZIERUNG_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_BERATER, filter: {}, settings: { fieldList: 'tid, mail, name, strasse, plz, ort, telefon, extern, login, hwk, plz_bereiche, bereich_key' }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_BERATER_BEREICHE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_KENNZAHLEN_IMPORT, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_FAKTOREN_INTERN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_KLIMAGASE_ART, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_FAKTOREN_KLIMAGASE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_MATERIAL, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_GUETER, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_REISEN_UEBERNACHTUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_REISEN_ENTFERNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ABFAELLE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_PENDELN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_TRANSPORT, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_TRANSPORT_UNTERKUNFT, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ZAEHLER_MEDIUM, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ANLAGEN_EIGENSTROM_TYPEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_BUNDESLAND, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFF_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFF_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_ENERGIETRAEGER_HEIZUNG_ELEKTRISCH, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WASSER_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_MASCHINEN_TYPEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ANGEMIETETE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ANGEMIETETE_FAHRZEUGE, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ONLINE_HILFE, filter: {}, settings: { fieldList: 'name, modul, info', resolve: false }, module: IDs.MOD_ETOOL_ADMIN },

            // AB HIER LEGACY
            // { entity: IDs.ENT_GROUP_ITEMS, filter: {}, settings: {}, module: IDs.MOD_LEGACY },
            // { entity: IDs.ENT_USER, filter: {}, settings: { resolve: true }, module: IDs.MOD_LEGACY },

            // UNTERNEHMENSDATEN
            { entity: IDs.ENT_UNTERNEHMENSDATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_UNTERNEHMENSDATEN_TOCHTERGESELLSCHAFTEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_WEITERE_GEWERKE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL }
        ];

        // Lade IDs.ENT_REGISTRIERUNG bedingungsweise: Admin oder Berater hat mandant_key, der Benutzer hat aber tid.
        if ((this.apiService.isAdmin() || this.apiService.isBerater() || this.apiService.userHasMultiTools) && this.apiService.getMandantKey()) {
            loadInfo.push({ entity: IDs.ENT_REGISTRIERUNG, filter: { mandant_key: this.apiService.getMandantKey() }, settings: { keepMandantKey: 1 }, module: IDs.MOD_ETOOL_ADMIN });
        } else if (this.apiService.getUserTid()) {
            loadInfo.push({ entity: IDs.ENT_REGISTRIERUNG, filter: { tid: this.apiService.getUserTid() }, settings: { keepMandantKey: 1 }, module: IDs.MOD_ETOOL_ADMIN });
        }

        // SCOPE3 einfügen
        const bereichKey = this.apiService.getBereichKey();
        const scope3Structur = [
            ...getCO2Scope3StructureVorgelagert(bereichKey),
            ...getCO2Scope3StructureNachgelagert(bereichKey),
            ...getCO2Scope3StructureAllgemein()
        ];
        scope3Structur.forEach((s: any) => {
            if (s.ignore) {
                return;
            }
            loadInfo.push({ entity: `co2_scope3_${s.key}`, filter: {}, settings: {}, module: IDs.MOD_ETOOL });
        });

        // VSME einfügen
        const vsmeStructur = [
            ...getVsmeBasisEntities(),
            ...getVsmeBpmEntities(),
            ...getVsmePatEntities(),
        ];
        vsmeStructur.filter((s: any) => s.key).forEach((s: any) => {
            loadInfo.push({ entity: s.key, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL });
        });

        loadInfo = this.removeDuplicate(loadInfo);
        this.loadGoal = loadInfo.length;
        this.loadCnt = 0;
        this.data = {};

        if (this.devFeaturesMap.N145_MAINLOADER_TIMEOUT) {
            loadInfo.map((i: any, idx: number) => setTimeout(() => this.processCall(i.entity, i.method, i.filter, i.settings, i.module, this.dataLoaded), 20 * idx));
        } else {
            loadInfo.map((i: any) => this.processCall(i.entity, i.method, i.filter, i.settings, i.module, this.dataLoaded));
        }

        // this.batchProcessCall(loadInfo);
    }

    batchProcessCall(loads: Array<any>): void {
        const loads_etool: Array<any> = [];
        const loads_admin: Array<any> = [];
        loads.forEach((item: any) => {
            item.settings = { ...item.settings, ...{ resolve: true, bundle: true } };
            // Calls nach Module aufteilen
            if (item.module === IDs.MOD_ETOOL) {
                loads_etool.push(item);
            } else if (item.module === IDs.MOD_ETOOL_ADMIN) {
                loads_admin.push(item);
            } else {
                console.log('Unbekannte Module:', item);
            }
            // Set triggerService.register
            const trigger_func = item.module === IDs.MOD_ETOOL_ADMIN ? 'admin_reload' : 'reload';
            this.triggerService.register(trigger_func, item.entity, (cbFromCaller?: (data: any) => void) => {
                this.processCall(item.entity, item.method, item.filter, item.settings, item.module, (obj: any) => {
                    this.dataLoaded(obj, item.entity);
                    if (typeof cbFromCaller === 'function') {
                        cbFromCaller(obj);
                    }
                }, true);
            });
        });
        // Calls nach Module separat senden
        if (loads_etool.length > 0) {
            this.batchSendCall(loads_etool, IDs.MOD_ETOOL);
        }
        if (loads_admin.length > 0) {
            this.batchSendCall(loads_admin, IDs.MOD_ETOOL_ADMIN);
        }
    }

    batchSendCall(loads: Array<any>, module: string) {
        const packSize = 10; // idealweise zwischen 5 un 20
        const subLoads: Array<any> = [];
        // Alle 10 Calls in einem Batch packen
        // console.log(loads, module);
        for (let i = 0; i < loads.length; i++) {
            subLoads.push(loads[i]);
            if (subLoads.length === packSize || i === loads.length - 1) {
                if (subLoads.length > 0) {
                    this.dataService.batchLoadData(subLoads, module, (data: any) => {
                        Object.keys(data).forEach((entity: string) => {
                            this.dataLoaded(data[entity], entity);
                        });
                    });
                }
                // console.log(i, [...subLoads]);
                subLoads.length = 0;
            }
        }
    }

    removeDuplicate(items: Array<any>) {
        const copyItems: Array<any> = [];
        items.forEach((itm: any) => {
            const fIdx = copyItems.findIndex((fI: any) => fI.entity === itm.entity);
            if (fIdx >= 0) {
                // console.log('Ein doppeltes Element wurde aus der Ladeliste entfernt:', itm.entity);
                // console.log(copyItems[fIdx], itm);
                copyItems[fIdx] = itm;
            } else {
                copyItems.push(itm);
            }
        });
        return copyItems;
    }

    loadDataForGewerk(useCluster: boolean, gewerk: number, betriebliche_situation: string, ort_tid: number): void {
        let loadInfo: any[] = undefined;
        // console.log('loadDataForGewerk: ', useCluster, gewerk, betriebliche_situation);
        this.data['info_gewerk'] = {};
        this.data['info_gewerk']['gewerk_use_cluster'] = useCluster;
        this.data['info_gewerk']['gewerk'] = gewerk;
        this.data['info_gewerk']['betriebliche_situation'] = betriebliche_situation;
        // console.log('Info Gewerk', this.data['info_gewerk']);
        // console.log('ORT:', ort_tid);
        if (!useCluster) { // also Gewerk
            loadInfo = [
                {
                    entity: IDs.ENT_KENNZAHLEN_DEFINTION, filter: { branchen_gewerke_tid: gewerk }, settings: { resolve: true },
                    module: IDs.MOD_ETOOL_ADMIN
                },
                {
                    entity: IDs.ENT_KENNZAHLEN_WERTE_AGGREGATION, filter: { branchen_gewerke_tid: gewerk }, settings: { resolve: true },
                    module: IDs.MOD_ETOOL_ADMIN
                },
                { entity: IDs.ENT_GTZ, filter: { ort_tid }, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },

            ];
        } else {
            loadInfo = [
                {
                    entity: IDs.ENT_KENNZAHLEN_DEFINTION, filter: { betriebliche_situation }, settings: { resolve: true },
                    module: IDs.MOD_ETOOL_ADMIN
                },
                {
                    entity: IDs.ENT_KENNZAHLEN_WERTE_AGGREGATION, filter: { betriebliche_situation }, settings: { resolve: true },
                    module: IDs.MOD_ETOOL_ADMIN
                },
                { entity: IDs.ENT_GTZ, filter: { ort_tid }, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            ];
        }
        //  console.log('GW Loadinfo:' , loadInfo);
        this.loadGoalGewerk = loadInfo.length;
        loadInfo.map((i: any) => this.processCall(
            i.entity, i.method, i.filter, i.settings, i.module,
            (result: any, entity) => this.dataLoaded(result, entity, true) // true = for Gewerk
        ));
    }

    async process(): Promise<void> {
        console.log('MainFilterLoader :: process()');
        const year = new Date().getFullYear();
        this.data['reloadData'] = this.loadData;
        this.data['callback'] = this.callback;
        this.data['this_year'] = year;
        this.data['last_year'] = year - 1; // <--- gegen dieses Jahr müssen wir prüfen
        processPlausibilisierung(this.data);
        /*
        this.processGtzStandort(() => {
            console.log('start Kennziffernberechnung');
            processKennziffernBerechnung(this.data);
            processZertifizierung(this.data);
            processSteuer(this.data);
            processCO2Mehrkosten(this.data, this.structureService);
            processHinweise(this.data);
            processThg(this.data, this.apiService.getBereichKey());
            processVsme(this.data);
            processKennzahlenExport(this.data, this.dataService, this.triggerService);
            const jahre: number[] = Object.values((this.data || {})[IDs.KENNZIFFERN_BERECHNET] || {})
                .filter((item: any) => item.co2_fussabdruck_total && this.data[IDs.KENNZIFFERN_BERECHNET][item.jahr])
                .map((item: any) => item.jahr);
            this.data['first_year'] = jahre.length ? Math.min(...jahre) : null;
        });
        */

        try {
            // Jetzt ohne Race-Condition
            // Abruf über KMU-API hatt Kennziffern nur alle paar male berechnet
            await this.processGtzStandort();

            console.log('start Kennziffernberechnung');
            processKennziffernBerechnung(this.data);
            processZertifizierung(this.data);
            processSteuer(this.data);
            processCO2Mehrkosten(this.data, this.structureService);
            processHinweise(this.data);
            processThg(this.data, this.apiService.getBereichKey());
            processVsme(this.data);
            processKennzahlenExport(this.data, this.dataService, this.triggerService);
            const jahre: number[] = Object.values((this.data || {})[IDs.KENNZIFFERN_BERECHNET] || {})
                .filter((item: any) => item.co2_fussabdruck_total && this.data[IDs.KENNZIFFERN_BERECHNET][item.jahr])
                .map((item: any) => item.jahr);
            this.data['first_year'] = jahre.length ? Math.min(...jahre) : null;

        } catch (err) {
            console.error("Fehler beim Laden der GTZ Standorte", JSON.stringify(err));
        }

        if (this.loadCnt === this.loadGoal) {
            processWetterstationen(this.data, this.triggerService);
        }
        await this.processWitterung();
        this.contextService.set('systemparameter', this.data[IDs.ENT_SYSTEMPARAMETER] || []);
        await this.setStandardPvWerte(this.data);
        this.callback(this.data);
    }

    processCall(entity: any, method: string, filter: any, settings: any, module: string, callback: (obj: any, entity: string) => void, isReload = false): void {
        const mod_use = module ? module : this.module;
        const trigger_func = module === IDs.MOD_ETOOL_ADMIN ? 'admin_reload' : 'reload';
        if (!isReload) {
            // Hier registrieren wir uns mit der Entity beim Trigger-Service.
            // damit können wir bei Änderungen der Entity automatisch nur diese Reloaden
            this.triggerService.register(trigger_func, entity, (cbFromCaller?: (data: any) => void) => {
                this.processCall(entity, method, filter, settings, module, (obj: any) => {
                    // console.log('MainLoader: Reloaded', entity, method, filter, settings, obj);
                    this.dataLoaded(obj, entity);
                    if (typeof cbFromCaller === 'function') {
                        cbFromCaller(obj);
                    }
                }, true);
            });
        }

        if (method) {
            this.apiService.call(
                method,
                {
                    filter,
                    module: mod_use,
                    override: 1,
                    settings
                },
                (json: string, suc: boolean) => {
                    if (suc) {
                        let obj: any;

                        try {
                            obj = JSON.parse(json);
                        } catch (e) {
                            console.error(e, json);
                            alert('Keine Gültigen Daten erhalten! Bitte Laden sie die Seite erneut!');
                        }

                        if (obj) {
                            if (typeof entity === 'string') {
                                callback(obj, entity);
                            } else if (Array.isArray(entity)) {
                                entity.forEach((ent: string) => {
                                    callback(obj, ent);
                                });
                            }
                        }
                    } else {
                        // console.log('Error Loading ', entity, method);
                        alert('Keine Gültigen Daten erhalten! Bitte Laden sie die Seite erneut!');
                    }
                }
            );
        } else {
            this.dataService.loadData(entity, filter, { ...settings, ...{ resolve: true, bundle: true } }, callback, mod_use);
        }
    }

    // Jetzt ohne Race-Condition
    async processGtzStandort(): Promise<void> {
        if (!this.data[IDs.ENT_GTZ_STANDORT]) {
            this.data[IDs.ENT_GTZ_STANDORT] = {};
        }

        const standorte: any[] = (this.data[IDs.ENT_STANDORT] || []).filter((standort: any) =>
            standort && (standort.energiedaten_erfassen_orig || standort.energiedaten_erfassen) === 1 && 
            standort.ort_gtz && !this.data[IDs.ENT_GTZ_STANDORT][standort.ort_gtz]
        );

        if (standorte.length === 0) {
            return; // Nichts zu tun, Promise ist gelöst
        }

        // Wir erstellen ein Array von Promises
        const promises = standorte.map((standort: any) => {
            return new Promise<void>((resolve) => {
                const ort_tid: number = ((this.data[IDs.ENT_GTZ_ORTE] || []).find((ort: any) => standort.ort_gtz === ort.ort) || {}).tid;
                
                // Wir nutzen das vorhandene loadData, kapseln es aber in ein Promise
                this.dataService.loadData(IDs.ENT_GTZ, { ort_tid }, {}, (result: any) => {
                    this.data[IDs.ENT_GTZ_STANDORT][standort.ort_gtz] = (result || {})[IDs.ENT_GTZ] || [];
                    resolve(); // Dieser eine Call ist fertig
                }, IDs.MOD_ETOOL_ADMIN);
            });
        });

        // WICHTIG: Warte, bis ALLE Promises im Array erledigt sind
        await Promise.all(promises);
    }


    processGtzStandortOld(callback: () => void): void {
        if (!this.data[IDs.ENT_GTZ_STANDORT]) {
            this.data[IDs.ENT_GTZ_STANDORT] = {};
        }
        const standorte: any[] = (this.data[IDs.ENT_STANDORT] || []).filter((standort: any) =>
            standort && (standort.energiedaten_erfassen_orig || standort.energiedaten_erfassen) === 1 && standort.ort_gtz
            && !this.data[IDs.ENT_GTZ_STANDORT][standort.ort_gtz]);

        if (!standorte.length && typeof callback === 'function') {
            callback();
        }

        standorte.forEach((standort: any, index: number) => {
            const ort_tid: number = ((this.data[IDs.ENT_GTZ_ORTE] || []).find((ort: any) => standort.ort_gtz === ort.ort) || {}).tid;
            this.dataService.loadData(IDs.ENT_GTZ, { ort_tid }, {}, (result: any) => {
                this.data[IDs.ENT_GTZ_STANDORT][standort.ort_gtz] = (result || {})[IDs.ENT_GTZ] || [];

                if (index === (standorte.length - 1) && typeof callback === 'function') {
                    callback();
                }
            }, IDs.MOD_ETOOL_ADMIN);
        });
    }

    async processWitterung(): Promise<void> {
        const gtz_orte: any[] = this.data[IDs.ENT_GTZ_ORTE] || [];
        const unternehmensdaten: any[] = this.data[IDs.ENT_UNTERNEHMENSDATEN] || [];
        const standorte: any[] = this.data[IDs.ENT_STANDORT] || [];
        
        this.data[IDs.ENT_WITTERUNGSFAKTOREN_STANDORT] = {};

        // Falls nichts zu tun ist, einfach beenden
        if (!unternehmensdaten.length && !standorte.length) {
            return; 
        }

        // Hilfsfunktion, die für EIN Element ein Promise liefert
        const fetchWetterForElement = (item: any, isStandort: boolean): Promise<void> => {
            return new Promise((resolve) => {
                const ort: any = gtz_orte.find((o: any) => o.ort === (item.ort_gtz || null)) || {};

                if (Object.keys(ort).length > 0) {
                    // Hier die API-Funktion aufrufen
                    getWitterungsFaktor(this.dataService, ort, (wdata: any) => {
                        if (isStandort) {
                            this.data[IDs.ENT_WITTERUNGSFAKTOREN_STANDORT][item.tid] = wdata;
                        } else {
                            this.data[IDs.ENT_WITTERUNGSFAKTOREN] = wdata;
                        }
                        resolve(); // Einzelner Call fertig
                    });
                } else {
                    resolve(); // Kein Ort gefunden, trotzdem fertig
                }
            });
        };

        // Wir sammeln alle Aufgaben in einem Array von Promises
        const tasks: Promise<void>[] = [];

        // 1. Unternehmensdaten (Hauptstandort)
        if (unternehmensdaten.length > 0) {
            tasks.push(fetchWetterForElement(unternehmensdaten[0], false));
        }

        // 2. Alle Standorte
        standorte.forEach(s => {
            tasks.push(fetchWetterForElement(s, true));
        });

        // WICHTIG: Warte auf alle Wetter-Abrufe!
        await Promise.all(tasks);
    }


    processWitterungOld(): void {
        const gtz_orte: any[] = this.data[IDs.ENT_GTZ_ORTE] || [];
        const unternehmensdaten: any[] = this.data[IDs.ENT_UNTERNEHMENSDATEN] || [];
        const standorte: any[] = this.data[IDs.ENT_STANDORT] || [];
        const setWitterungsfaktoren = (data: any[], isStandort?: boolean, setState?: boolean): void => {
            [...(isStandort ? data : [data[0] || {}])].forEach((item: any) => {
                const ort: any = gtz_orte.find((o: any) => o.ort === (item.ort_gtz || null)) || {};
                if (Object.keys(ort).length) {
                    getWitterungsFaktor(this.dataService, ort, (wdata: any) => {
                        if (isStandort) {
                            this.data[IDs.ENT_WITTERUNGSFAKTOREN_STANDORT][item.tid] = wdata;
                        } else {
                            this.data[IDs.ENT_WITTERUNGSFAKTOREN] = wdata;
                        }
                        setState && this.callback(this.data);
                    });
                } else {
                    setState && this.callback(this.data);
                }
            });
        };
        this.data[IDs.ENT_WITTERUNGSFAKTOREN_STANDORT] = {};

        if (!unternehmensdaten.length && !standorte.length) {
            this.callback(this.data);
            return;
        }
        setWitterungsfaktoren(unternehmensdaten);
        setWitterungsfaktoren(standorte, true, true);
    }

    setStandardKonfiguration(data: any[]): void {
        const entity = IDs.ENT_KONFIGURATION;
        let regiLoadFilter;
        if ((this.apiService.isAdmin() || this.apiService.isBerater() || this.apiService.userHasMultiTools) && this.apiService.getMandantKey()) {
            regiLoadFilter = { mandant_key: this.apiService.getMandantKey() };
        } else if (this.apiService.getUserTid()) {
            regiLoadFilter = { tid: this.apiService.getUserTid() };
        }
        if (data.length === 0 && regiLoadFilter) {
            // console.log('GF MFL ...');
            this.dataService.loadData(IDs.ENT_REGISTRIERUNG, regiLoadFilter, { fieldList: 'unternehmen, test_registrierung' }, (data: any) => {
                const test_registrierung: number = +((data[IDs.ENT_REGISTRIERUNG] || [])[0] || {}).test_registrierung || null;
                this.dataService.saveData(
                    entity,
                    {
                        startjahr_datenerfassung: 2010,
                        anwendungskontext: 'energiebuch',
                        mehrere_standorte: 2,
                        veranstaltungen: 2,
                        alternatives_system: 2,
                        daten_standorte_gebaeude: 2,
                        daten_maschinen: 1,
                        daten_fahrzeuge: 1,
                        daten_elektrofahrzeuge: 2,
                        daten_erneuerbare_energien: 2,
                        daten_kraftstoffe_standort: 'hauptsitz',
                        daten_zustaendigkeiten: 2,
                        start_hinweis: 1,
                        gefuehrter_modus: 1,
                        berater_zugriff: 2,
                        externer_berater: 2,
                        unternehmensart: 'handwerksbetrieb',
                        co2_fussabdruck: 2,
                        co2_fussabdruck_modus: 'simple',
                        etool_test: test_registrierung === 1 ? 1 : 2
                    }, null, false,
                    (suc: boolean, saveTid: number) => {
                        if (suc && saveTid > 0) {
                            this.triggerService.fire('reload', entity);
                        }
                    },
                    this.module
                );
            },
                IDs.MOD_ETOOL_ADMIN
            );
        } else {
            const config: any = data[0];
            if (!config.co2_fussabdruck) { // TODO Nochmal richtig machen, das hier ist nur ein Workaround...
                config.co2_fussabdruck = 'Nein';
                config.co2_fussabdruck_orig = 2;
            }
            // console.log('Conf', config);

            // Bei Kultur soll GHG-Modus immer gewählt sein.
            const kultur_flag = this.apiService.getBereichKey() === 'kultur';
            if (config?.tid > 0 && kultur_flag && config.co2_fussabdruck_modus !== 'ghg') {
                this.dataService.saveData(IDs.ENT_KONFIGURATION, { tid: config.tid, co2_fussabdruck_modus: 'ghg' },
                    null, false, () => this.triggerService.fire('reload', IDs.ENT_KONFIGURATION), this.module);
            }
        }
    }

    setStandardKonfigurationKurz(data: any[]): void {
        if (data.length === 0) {
            // NOP
        } else {
            const config: any = data[0];
            if (!config.co2_fussabdruck) {
                // TODO Nochmal richtig machen, das hier ist nur ein Workaround...
                config.co2_fussabdruck = 'Nein';
                config.co2_fussabdruck_orig = 2;
            }
            const kultur_flag = this.apiService.getBereichKey() === 'kultur';
            const handwerk_flag = this.apiService.getBereichKey() === 'handwerk';
            const dataUpdate: any = { tid: config.tid };
            const isCfm: boolean = config.dateneingabe_variante === 'cfm';
            let doUpdate = false;
            // Bei Kultur soll GHG-Modus immer gewählt sein.
            // Wenn das CFM Modul aktiviert ist, muss automatisch die detaillierte Erfassung aktiviert sein!
            if ((isCfm || kultur_flag) && config.co2_fussabdruck_modus !== 'ghg') {
                doUpdate = true;
                dataUpdate.co2_fussabdruck_modus = 'ghg';
            }
            // Bei Handwerk soll Vorsteuerabzugsberechtigt immer "Ja" sein.
            if (handwerk_flag && config.vorsteuerabzugsberechtigt_orig !== 1) {
                doUpdate = true;
                dataUpdate.vorsteuerabzugsberechtigt = 1;
            }
            if (doUpdate && dataUpdate.tid > 0) {
                this.dataService.saveData(IDs.ENT_KONFIGURATION, dataUpdate, null, false, () => this.triggerService.fire('reload', IDs.ENT_KONFIGURATION), this.module);
            }
        }
    }

    async setStandardPvWerte(data: any): Promise<void> {
        const entity: string = IDs.ENT_PV_EINGABE;
        const pv_eingabe: any = data[entity] || [];

        if (!pv_eingabe.length) {
            const structure: any = this.structureService.getStructureByKey(this.module, entity);
            const systemparameter: any = {};
            const values: any = {};

            (data[IDs.ENT_SYSTEMPARAMETER] || []).forEach((element: any) => {
                systemparameter[element.parameter] = element.wert;
            });

            (structure.ATTS || []).forEach((element: any) => {
                const key: string = element.KEY;

                if (key !== 'modus' && systemparameter[`pv_${key}`]) {
                    values[key] = systemparameter[`pv_${key}`] || null;
                }
            });
            
            return new Promise<void>((resolve, reject) => {
                this.dataService.saveDataList(entity,
                    [
                        { ...values, modus: 0 }, // Standard
                        { ...values, modus: 1 } // Experte
                    ],
                    (sql: string, success: boolean) => {
                        if (success) {
                            // alert('TRIGGER PV');
                            this.triggerService.fire('reload', entity);
                            resolve();
                        } else {
                            resolve();
                        }
                    },
                    this.module
                );
            });
        } else {
            return Promise.resolve();
        }
    }

    setStandardStandort(data: any[]): void {
        const entity = IDs.ENT_STANDORT;
        if (data.length === 0) {
            this.dataService.saveData(entity,
                {
                    name: 'Hauptsitz',
                    standort_typ_tid: 1,
                    energiedaten_erfassen: 1
                }, null, false,
                (suc: boolean, saveTid: number) => {
                    if (suc && saveTid > 0) {
                        this.triggerService.fire('reload', entity);
                    }
                },
                this.module
            );
        }
    }

    setStandardUnternehmensdaten(data: any[]): void {
        const entity = IDs.ENT_UNTERNEHMENSDATEN;
        const isAdmin = this.apiService.isAdmin();
        const isBerater = this.apiService.isBerater();
        const email = !isAdmin && !isBerater ? this.apiService.getUsername() : null;
        if (!email) {
            return;
        }
        if (data.length === 0) {
            this.dataService.loadData(IDs.ENT_REGISTRIERUNG, { mail: email }, { fieldList: 'unternehmen, bereich_key' }, (data: any) => {
                const regi = Array.isArray(data[IDs.ENT_REGISTRIERUNG]) && data[IDs.ENT_REGISTRIERUNG].length > 0 ? data[IDs.ENT_REGISTRIERUNG][0] : {};
                const unternehmen: number = regi.unternehmen || 'Mein Unternehmen';
                let bereich_key: string = regi.bereich_key || '';
                // if (this.config.preset_bereich) {
                //     bereich_key = this.config.preset_bereich;
                // }
                this.dataService.saveData(entity, { name: unternehmen, email, bereich_key }, null, false, (suc: boolean, saveTid: number) => {
                    if (suc && saveTid > 0) {
                        this.triggerService.fire('reload', entity);
                    }
                }, this.module);
            }, IDs.MOD_ETOOL_ADMIN);
        } else if (data.length === 1) {
            // if (this.config.preset_bereich && data[0].bereich_key !== this.config.preset_bereich) {
            //     this.dataService.saveData(entity, { tid: data[0].tid, bereich_key: this.config.preset_bereich }, null, false, (suc: boolean, saveTid: number) => {
            //         if (suc) {
            //             this.triggerService.fire('reload', entity);
            //         }
            //     }, this.module);
            // }
            if (!data[0].email) {
                this.dataService.saveData(entity, { tid: data[0].tid, email }, null, false, (suc: boolean, saveTid: number) => {
                    if (suc) {
                        this.triggerService.fire('reload', entity);
                    }
                }, this.module);
            }
        }
    }

    // Wir registrieren hier ein Event, mit dem man die lokalen Daten teilweise ändern kann.
    registerLocalDataUpdate() {
        this.triggerService.register('updateLocalDataByEntity', 'dataObject', (newData: any) => {
            Object.keys(newData).forEach((ent: string) => {
                if (this.data.hasOwnProperty(ent) && Array.isArray(this.data[ent]) && newData[ent].tid > 0) {
                    const idx = this.data[ent].findIndex((r: any) => r.tid === newData[ent].tid);
                    if (idx >= 0) {
                        Object.keys(newData[ent]).forEach((att: string) => {
                            if (this.data[ent][idx].hasOwnProperty(att)) {
                                // Bugfix: renderDatensatzForSelect() zeigt bei Änderungen der Formulardaten die TID anstelle des Namens an
                                if (this.data[ent][idx].hasOwnProperty(`${att}_orig`) && typeof this.data[ent][idx][att] === 'string') {
                                    this.data[ent][idx][`${att}_display`] = this.data[ent][idx][att];
                                }
                                // console.log(att, this.data[ent][idx][att], '->', newData[ent][att]);
                                this.data[ent][idx][att] = newData[ent][att];
                            }
                        });
                    }
                }
            });
            this.callback(this.data);
        });
    }

    processKlimagaseFaktoren(): void {
        const data: any = this.data || {};
        if (Array.isArray(data[IDs.ENT_KLIMAGASE_ART]) && Array.isArray(data[IDs.ENT_CO2_FAKTOREN_KLIMAGASE])) {
            data[IDs.ENT_CO2_FAKTOREN_KLIMAGASE].forEach((f: any) => {
                if (f.art_orig > 0) {
                    const kFnd = data[IDs.ENT_KLIMAGASE_ART].find((k: any) => k.tid === f.art_orig);
                    if (kFnd) {
                        f.name = kFnd.name;
                        f.info = kFnd.info;
                    }
                }
            });
        }
        // console.log(data[IDs.ENT_CO2_FAKTOREN_KLIMAGASE]);
    }

    checkKonfigKonsis(): void {
        const data: any = this.data || {};
        if (Array.isArray(data[IDs.ENT_KONFIGURATION]) && data[IDs.ENT_KONFIGURATION].length > 0
            && Array.isArray(data[IDs.ENT_REGISTRIERUNG]) && data[IDs.ENT_REGISTRIERUNG].length > 0) {
            const konfiguration: any = data[IDs.ENT_KONFIGURATION][0];
            const registrierung: any = data[IDs.ENT_REGISTRIERUNG][0];

            // Konfig und Regi müssen sich darüber einig sein, ob es sich um ein Testtool handelt.
            const test_registrierung: number = +registrierung.test_registrierung === 1 ? 1 : 2;
            const etool_test: number = typeof konfiguration.etool_test_orig !== 'undefined'
                ? +konfiguration.etool_test_orig
                : +konfiguration.etool_test;
            if (etool_test !== test_registrierung && this.checkedTestRegistrierung === false && konfiguration?.tid > 0) {
                console.log('Update Konfig.etool_test', etool_test, '->', test_registrierung);
                this.dataService.saveData(IDs.ENT_KONFIGURATION, { tid: konfiguration.tid, etool_test: test_registrierung }, null, false, () => {
                    this.triggerService.fire('admin_reload', IDs.ENT_REGISTRIERUNG, () =>
                        this.triggerService.fire('reload', IDs.ENT_KONFIGURATION)
                    );
                }, this.module);
            }
            // Die Konsistenz von test_registrierung und etool_test wird nur einmal ganz am Anfang überprüft und danach nicht mehr, 
            // da Admins den Wert ändern dürfen.
            this.checkedTestRegistrierung = true;

            // Das Steuermodul erzwingt die Verwendung des geführten Modus.
            if (konfiguration.dateneingabe_variante === 'steuer' && konfiguration.gefuehrter_modus_orig !== 1 && konfiguration.tid > 0) {
                this.dataService.saveData(IDs.ENT_KONFIGURATION, { tid: konfiguration.tid, gefuehrter_modus: 1 }, null, false,
                    () => this.triggerService.fire('reload', IDs.ENT_KONFIGURATION), this.module);
            }

            // Es soll nun nur noch den detaillierten (ghg) Modus geben, bei allen Tools.
            if (konfiguration.tid > 0 && konfiguration.co2_fussabdruck_modus !== 'ghg') {
                this.dataService.saveData(IDs.ENT_KONFIGURATION, { tid: konfiguration.tid, co2_fussabdruck_modus: 'ghg' }, null, false,
                    () => this.triggerService.fire('reload', IDs.ENT_KONFIGURATION), this.module);
            }

        }
    }

    loadDataKMU(): void {
        this.start = new Date().getTime();
        let loadInfo: Array<any> = [
            // { entity: IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_BIOMASSE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_KWK, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_PHOTOVOLTAIK, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_SOLARTHERMIE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_WAERMEPUMPEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_WINDKRAFT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_BETRIEBSDATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_DATEIABLAGE_DYNAMISCH, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_CUSTOM_ROW_NAME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_EINZELMASSNAHMEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_EIGENSTROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_EIGENWAERME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFFE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_STROM, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_VERBRAEUCHE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_WASSER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ENERGIE_COCKPIT_HINWEISE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_CO2_SCOPE3_LIEFERANT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_FAHRZEUGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_FAHRZEUGE_JAHRESWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_GEBAEUDE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KENNZAHLEN_DYNAMISCH, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KENNZAHLEN_WERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KONFIGURATION, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_MASCHINEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_MASCHINEN_JAHRESWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_OEL_ABRECHNUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_OEL_ZAEHLERABLESUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_POPUP_BLOCK, filter: { user: this.apiService.getUsername() }, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_PV_EINGABE, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_REPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_SONSTIGE_DATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_STANDORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_STANDORT_GRUPPE, filter: {}, settings: {}, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_STANDORT_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ZAEHLER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_ZERTIFIZIERUNGSOPTIONEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_KLIMAGASE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_KLIMAGASE_BENUTZER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CO2_SICHERHEITSZUSCHLAG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_CO2_SCOPE3_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ENERGIEDATEN_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANGEMIETETE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANGEMIETETE_FAHRZEUGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_EIGENE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_ANABREISE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_CUSTOM_DATENSATZ_REIHENFOLGE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // CFM
            // { entity: IDs.ENT_CFM_MOTIVATION, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // VSME
            // { entity: IDs.ENT_VSME_N5, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_VSME_AUSWAHL, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_VSME_BPM3_SCOPE1, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_VSME_BPM3_SCOPE2, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            { entity: IDs.ENT_VSME_EXPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_VSME_REPORT, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },

            // ACHTUNG ab hier aus ETOOL_ADMIN
            { entity: IDs.ENT_BRANCHEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_BRANCHEN_BEREICHE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_BRANCHEN_GEWERKE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_GEWERKE_WZ, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_DATEIEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ERZEUGUNGS_LASTDATEN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_FOERDERUNG, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_FOERDERUNG_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_PARTNER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_PARTNER_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_GTZ_ORTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_NACE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARAMETER_ENERGIESTEUER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PARAMETER_HEIZWERTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_PV_PARAMETER, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_EEG_VERGUETUNG, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_QUELLEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_SYSTEMPARAMETER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_PLAUSIBILISIERUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_SYSTEMTEXTE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_SYSTEMTEXTE_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ZERTIFIZIERUNG_ZUORDNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_BERATER, filter: {}, settings: { fieldList: 'tid, mail, name, strasse, plz, ort, telefon, extern, login, hwk, plz_bereiche, bereich_key' }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_BERATER_BEREICHE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_KENNZAHLEN_IMPORT, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_FAKTOREN_INTERN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_FAKTOREN_KLIMAGASE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_MATERIAL, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_GUETER, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_REISEN_UEBERNACHTUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_REISEN_ENTFERNUNG, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ABFAELLE, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_PENDELN, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_TRANSPORT, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_TRANSPORT_UNTERKUNFT, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ZAEHLER_MEDIUM, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ANLAGEN_EIGENSTROM_TYPEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_BUNDESLAND, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFF_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ENERGIEDATEN_KRAFTSTOFF_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_ENERGIETRAEGER, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_ENERGIEDATEN_WAERME_ENERGIETRAEGER_HEIZUNG_ELEKTRISCH, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_ENERGIEDATEN_WASSER_EINHEITEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL_ADMIN },
            // { entity: IDs.ENT_MASCHINEN_TYPEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ANGEMIETETE_RAEUMLICHKEITEN, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_CO2_SCOPE3_FAKTOREN_ANGEMIETETE_FAHRZEUGE, filter: {}, settings: { resolve: false }, module: IDs.MOD_ETOOL_ADMIN },
            { entity: IDs.ENT_EXTERNAL_ACCESS, filter: {}, settings: {resolve: false}, module: IDs.MOD_ETOOL_ADMIN},
            { entity: IDs.ENT_KLIMAGASE_ART, filter: {}, settings: {}, module: IDs.MOD_ETOOL_ADMIN },

            // UNTERNEHMENSDATEN
            { entity: IDs.ENT_UNTERNEHMENSDATEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_UNTERNEHMENSDATEN_TOCHTERGESELLSCHAFTEN, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL },
            // { entity: IDs.ENT_WEITERE_GEWERKE, filter: {}, settings: { resolve: true }, module: IDs.MOD_ETOOL }
        ];

        // Lade IDs.ENT_REGISTRIERUNG bedingungsweise: Admin oder Berater hat mandant_key, der Benutzer hat aber tid.
        if ((this.apiService.isAdmin() || this.apiService.isBerater() || this.apiService.userHasMultiTools) && this.apiService.getMandantKey()) {
            loadInfo.push({ entity: IDs.ENT_REGISTRIERUNG, filter: { mandant_key: this.apiService.getMandantKey() }, settings: { keepMandantKey: 1 }, module: IDs.MOD_ETOOL_ADMIN });
        } else if (this.apiService.getUserTid()) {
            loadInfo.push({ entity: IDs.ENT_REGISTRIERUNG, filter: { tid: this.apiService.getUserTid() }, settings: { keepMandantKey: 1 }, module: IDs.MOD_ETOOL_ADMIN });
        }

        // SCOPE3 einfügen
        const bereichKey = this.apiService.getBereichKey();
        const scope3Structur = [
            ...getCO2Scope3StructureVorgelagert(bereichKey),
            ...getCO2Scope3StructureNachgelagert(bereichKey),
            ...getCO2Scope3StructureAllgemein()
        ];
        scope3Structur.forEach((s: any) => {
            if (s.ignore) {
                return;
            }
            loadInfo.push({ entity: `co2_scope3_${s.key}`, filter: {}, settings: {}, module: IDs.MOD_ETOOL });
        });

        loadInfo = this.removeDuplicate(loadInfo);
        this.loadGoal = loadInfo.length;
        this.loadCnt = 0;
        this.data = {};

        // loadInfo.forEach((i: any) => {
        //     this.processCall(i.entity, i.method, i.filter, i.settings, i.module, this.dataLoaded);
        // });

        this.batchProcessCall(loadInfo);
    }

    getLoaderFilter(item: any, filter: any): any {
        const loaderFilter: any = { ...(item.filter || {}) };
        if (filter && Object.keys(filter).length > 0) {
            const structure: any = this.structureService.getStructureByKey(item.module, item.entity);
            if (structure && Array.isArray(structure.ATTS)) {
                Object.keys(filter).forEach((key: string) => {
                    if (key === 'jahr' && item.entity.startsWith('co2_faktoren')) {
                        return;
                    }
                    if (structure.ATTS.find((att: any) => att.KEY === key)) {
                        loaderFilter[key] = filter[key];
                    }
                });
            } else {
                console.error('getStructureByKey', item.module, item.entity);
            }
        }
        return loaderFilter;
    }

}
